import { motion } from "framer-motion";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { CTASection } from "../components/CTASection";
import {
  IMAGES, MISSION, VISION, QUALITY, SAFETY, CORE_VALUES, MEANING_OF_KOTAM,
} from "../data/content";

const PageHero = ({ eyebrow, title, subtitle, image }) => (
  <section className="relative pt-36 pb-20 sm:pt-44 sm:pb-28 overflow-hidden bg-forest-dark text-white">
    <div className="absolute inset-0 opacity-25">
      <img src={image} alt="Kotam Projects construction team and premium builds across VIC and SA" className="h-full w-full object-cover" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-t from-forest-dark via-forest-dark/80 to-forest-dark/50" />
    <div className="container-px relative">
      <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="label text-gold">
        {eyebrow}
      </motion.p>
      <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }} className="mt-6 font-display font-light text-hero max-w-4xl">
        {title}
      </motion.h1>
      {subtitle && (
        <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="mt-8 max-w-2xl text-lg sm:text-xl text-white/70 leading-relaxed">
          {subtitle}
        </motion.p>
      )}
    </div>
  </section>
);

export default function About() {
  return (
    <div>
      <SEO
        title="About Kotam Projects | Trusted Builders in Victoria & South Australia"
        description="Kotam Projects is a licensed, fully insured building company delivering quality construction across Melbourne, Craigieburn, Adelaide and Pooraka. Discover our mission, values, and safety-first culture."
        path="/about"
      />
      <PageHero
        eyebrow="About Kotam Projects"
        title="Craftsmanship rooted in meaning."
        subtitle="A construction company built on integrity, excellence, and a name that carries centuries of meaning — shelter, strength, and permanence."
        image={IMAGES.blueprint2}
      />

      {/* Story + Meaning of Kotam */}
      <section className="container-px py-24 sm:py-32 lg:py-40">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-20 items-start">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="label text-gold">The Meaning of Kotam</p>
              <h2 className="mt-6 font-display font-light text-h2 text-charcoal">A name that means shelter and strength.</h2>
            </Reveal>
            <div className="mt-10 space-y-8">
              {MEANING_OF_KOTAM.map((m, i) => (
                <Reveal key={m.lang} delay={i * 0.12} className="border-l-2 border-gold pl-6">
                  <p className="font-display text-xl font-medium text-forest">{m.lang}</p>
                  <p className="mt-3 text-lg text-charcoal/60 leading-relaxed">{m.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <div className="grid grid-cols-2 gap-4">
                <img src="https://images.unsplash.com/photo-1748063578185-3d68121b11ff?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="Contemporary luxury residence at dusk" className="rounded-[20px] h-72 w-full object-cover" loading="lazy" />
                <img src="https://images.pexels.com/photos/37610869/pexels-photo-37610869.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1400" alt="Elegant staircase with glass balustrade" className="rounded-[20px] h-72 w-full object-cover mt-10" loading="lazy" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-concrete py-24 sm:py-32">
        <div className="container-px grid gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <span className="font-display text-6xl font-light text-gold/50">01</span>
            <p className="label text-forest mt-4">Mission</p>
            <p className="mt-6 font-display text-2xl sm:text-[28px] font-light leading-snug text-charcoal">{MISSION}</p>
          </Reveal>
          <Reveal delay={0.12}>
            <span className="font-display text-6xl font-light text-gold/50">02</span>
            <p className="label text-forest mt-4">Vision</p>
            <p className="mt-6 font-display text-2xl sm:text-[28px] font-light leading-snug text-charcoal">{VISION}</p>
          </Reveal>
        </div>
      </section>

      {/* Core Values */}
      <section className="container-px py-24 sm:py-32 lg:py-40">
        <Reveal>
          <p className="label text-gold">Core Values</p>
          <h2 className="mt-6 font-display font-light text-h2 text-charcoal max-w-2xl">The principles behind every build.</h2>
        </Reveal>
        <div className="mt-16 grid gap-px overflow-hidden rounded-[24px] bg-border sm:grid-cols-2 lg:grid-cols-5">
          {CORE_VALUES.map((v, i) => (
            <Reveal key={v.title} delay={(i % 5) * 0.06} className="bg-white p-8">
              <span className="font-display text-4xl font-light text-gold">0{i + 1}</span>
              <h3 className="mt-5 font-display text-xl font-semibold text-charcoal">{v.title}</h3>
              <p className="mt-3 text-charcoal/60 leading-relaxed">{v.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Our Home Base */}
      <section className="bg-concrete py-24 sm:py-32">
        <div className="container-px grid gap-14 lg:grid-cols-12 lg:gap-20 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="overflow-hidden rounded-[24px] shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
                <img src="/kotam-office.jpg" alt="Kotam Projects office and branded work ute" className="h-[520px] w-full object-cover" loading="lazy" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <p className="label text-gold">Our Home Base</p>
              <h2 className="mt-6 font-display font-light text-h2 text-charcoal">
                A proudly local team, on the road across VIC & SA.
              </h2>
              <p className="mt-8 text-lg text-charcoal/60 leading-relaxed">
                From our Craigieburn headquarters, our team delivers land development, project management,
                construction services and custom-built homes across Victoria and South Australia. As a
                Master Builders member and Registered Building Practitioner, we bring accredited expertise to
                every job — and you'll spot our vehicles on sites right across the region.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {["Land Development", "Project Management", "Construction Services", "Custom Built Homes"].map((t) => (
                  <span key={t} className="rounded-full border border-charcoal/15 bg-white px-5 py-2.5 text-sm font-medium text-charcoal">
                    {t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Quality & Safety */}
      <section className="container-px pb-24 sm:pb-32">
        <div className="grid gap-6 lg:grid-cols-2">
          <Reveal className="rounded-[24px] bg-forest text-white p-10 sm:p-14">
            <p className="label text-gold">Commitment to Quality</p>
            <p className="mt-6 text-lg text-white/80 leading-relaxed">{QUALITY}</p>
          </Reveal>
          <Reveal delay={0.12} className="rounded-[24px] border border-border bg-white p-10 sm:p-14">
            <p className="label text-gold">Safety First Culture</p>
            <p className="mt-6 text-lg text-charcoal/60 leading-relaxed">{SAFETY}</p>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
